import { APP_PERMISSIONS, type Permission } from "@spiriidk/connect-configs";

/** Recursively collects every string leaf of a (possibly nested) permission group. */
function flattenPermissions(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (value && typeof value === "object") {
    return Object.values(value).flatMap(flattenPermissions);
  }
  return [];
}

// Every permission-group leaf in the catalog, excluding ROLES (role labels,
// not permissions — see the doc comment below on why only one role is kept).
const ALL_CATALOG_PERMISSIONS = Object.entries(APP_PERMISSIONS)
  .filter(([group]) => group !== "ROLES")
  .flatMap(([, value]) => flattenPermissions(value)) as Permission[];

/**
 * The full grant: System Admin plus every leaf permission in the
 * `APP_PERMISSIONS` catalog. This covers every page-level and fine-grained
 * permission, including the two the smoke routes gate on
 * (`permission_truckcharge_view` for connect `/bookings/chargers`,
 * `permission_locations_findorder` for the tech-ui `/order-lookup` view). Only
 * the System Admin role label is included — other role labels are not
 * permissions, and holding several simultaneously could trip role-switch logic.
 */
const FULL_PERMISSIONS: Permission[] = Array.from(
  new Set<Permission>([
    APP_PERMISSIONS.ROLES.SYSTEM_ADMIN,
    ...ALL_CATALOG_PERMISSIONS,
  ])
);

/** Company id the default (company-scoped) session carries. */
const DEFAULT_COMPANY_ID = 1;

/**
 * Auth-profile seam for the hermetic e2e suite (FRO-363, design D5 / adr/0003
 * env-gated mock auth). One seeded user, selectable per Playwright test along
 * two axes so the migrated settings views' permission/scope gates can be
 * exercised without a real Keycloak or a second seeded user:
 *
 * - `profile` selects the PERMISSION SET — the full grant, or the full grant
 *   with one settings permission removed (to drive a route/tab/card gate).
 * - `scope` selects COMPANY vs OPERATOR scope — a company-scoped session carries
 *   a permitted company id; an operator-scoped one carries none. The branding
 *   cards gate on this (logo is operator-wide; the display name is a per-company
 *   override — research/01 §1.5).
 *
 * `AUTH_PERMISSION_PROFILES` maps each profile to the permissions REMOVED from
 * the full grant, so the default profile ("full") is byte-for-byte today's
 * full-access grant and existing specs are unaffected.
 */
const AUTH_PERMISSION_PROFILES = {
  full: [],
  "no-page-settings": [APP_PERMISSIONS.PAGE.SETTINGS],
  "no-branding-tab": [APP_PERMISSIONS.SETTINGS.BRANDING],
  "no-connectlogo": [APP_PERMISSIONS.SETTINGS.BRANDING_CONNECTLOGO],
  "no-brandname": [APP_PERMISSIONS.SETTINGS.BRAND_NAME],
  // Team-access profiles (FRO-400). The section's gates are the legacy
  // `TEAM_ACCESS.*` constants reused verbatim, so these drive both the legacy
  // `/team` view and the migrated `/settings/users|api-tokens` tabs — the
  // `@spiriidk/auth` singleton the router injects is shared with both remotes.
  "no-page-teamaccess": [APP_PERMISSIONS.PAGE.TEAM_ACCESS],
  // Holds the team page permission alone, so the Users tab's rule passes and the
  // section admits them while neither settings tab is offered: the
  // first-permitted-tab redirect must land on Users, not the Alerts default.
  "teamaccess-only": [
    APP_PERMISSIONS.PAGE.SETTINGS,
    APP_PERMISSIONS.SETTINGS.ALERTS,
    APP_PERMISSIONS.SETTINGS.BRANDING,
  ],
  // Holds `PAGE.SETTINGS` and satisfies no tab's rule. Since the section is
  // available only when one of its tabs is, this profile is DENIED it outright —
  // no navigation entry, and the direct URL renders the unauthorized state.
  "no-settings-tabs": [
    APP_PERMISSIONS.SETTINGS.ALERTS,
    APP_PERMISSIONS.SETTINGS.BRANDING,
    APP_PERMISSIONS.PAGE.TEAM_ACCESS,
    APP_PERMISSIONS.TEAM_ACCESS.API_KEYS_VIEW,
  ],
  // Neither page permission, so no tab rule can pass and the section is denied.
  "no-section-access": [
    APP_PERMISSIONS.PAGE.SETTINGS,
    APP_PERMISSIONS.PAGE.TEAM_ACCESS,
  ],
  "no-teamaccess-create": [APP_PERMISSIONS.TEAM_ACCESS.CREATE],
  "no-teamaccess-delete": [APP_PERMISSIONS.TEAM_ACCESS.DELETE],
  "no-apikeys-view": [APP_PERMISSIONS.TEAM_ACCESS.API_KEYS_VIEW],
  "no-apikeys-create": [APP_PERMISSIONS.TEAM_ACCESS.API_KEYS_CREATE],
  "no-apikeys-delete": [APP_PERMISSIONS.TEAM_ACCESS.API_KEYS_DELETE],
} satisfies Record<string, Permission[]>;

type AuthPermissionProfile = keyof typeof AUTH_PERMISSION_PROFILES;

/**
 * `all` is the third scope FRO-400 needs: the legacy Users table derives its
 * column set from the viewer's permitted operator/company ids (tech-ui's ad-hoc
 * SuperAdmin/OperatorAdmin/CompanyAdmin split — research/01 §11), so proving the
 * three column sets needs a session permitted on ALL operators as well as the
 * operator- and company-scoped ones that already existed.
 */
const AUTH_SCOPES = ["company", "operator", "all"] as const;
type AuthScope = (typeof AUTH_SCOPES)[number];

/** Operator id per scope: `all` carries none (0 = every operator). */
const SCOPE_OPERATOR_IDS: Record<AuthScope, number> = {
  company: 1,
  operator: 1,
  all: 0,
};

/** Company id per scope: only a company-scoped session carries one. */
const SCOPE_COMPANY_IDS: Record<AuthScope, number> = {
  company: DEFAULT_COMPANY_ID,
  operator: 0,
  all: 0,
};

interface AuthProfileSelection {
  profile: AuthPermissionProfile;
  scope: AuthScope;
}

/**
 * The default selection: the full grant, company-scoped. `buildTestUser` of this
 * selection reproduces the pre-seam test user exactly, so groups 1–5 specs and
 * behavior are untouched by the seam.
 */
const DEFAULT_AUTH_PROFILE: AuthProfileSelection = {
  profile: "full",
  scope: "company",
};

const AUTH_PERMISSION_PROFILE_NAMES = Object.keys(
  AUTH_PERMISSION_PROFILES
) as AuthPermissionProfile[];

/**
 * Seed data for the mock-auth test user (see `./mock-auth`).
 *
 * `roles` is the flat array Spirii Keycloak stores under
 * `tokenParsed["resource-access"].techportal.roles` — it mixes role labels
 * (e.g. "System Admin") with fine-grained permission strings, and
 * `resolveAuthData` exposes the whole array as `userPermissions`.
 */
interface TestUser {
  userId: string;
  username: string;
  email: string;
  name: string;
  operatorId: number;
  companyId: number;
  roles: Permission[];
}

/** Fields that never vary across profiles. */
const BASE_TEST_USER = {
  userId: "e2e-test-user",
  username: "e2e@spirii.com",
  email: "e2e@spirii.com",
  name: "E2E Test User",
} as const;

/**
 * Build the seeded test user for a profile selection. The permission set is the
 * full grant minus the profile's removed permissions; the scope decides the
 * permitted operator/company ids, where `0` is the falsy "all" marker the apps
 * read as unscoped.
 */
function buildTestUser({ profile, scope }: AuthProfileSelection): TestUser {
  const removed = new Set<Permission>(AUTH_PERMISSION_PROFILES[profile]);
  return {
    ...BASE_TEST_USER,
    operatorId: SCOPE_OPERATOR_IDS[scope],
    companyId: SCOPE_COMPANY_IDS[scope],
    roles: FULL_PERMISSIONS.filter((permission) => !removed.has(permission)),
  };
}

/** The default (full-access, company-scoped) test user. */
const TEST_USER: TestUser = buildTestUser(DEFAULT_AUTH_PROFILE);

export {
  TEST_USER,
  buildTestUser,
  DEFAULT_AUTH_PROFILE,
  AUTH_SCOPES,
  AUTH_PERMISSION_PROFILE_NAMES,
};
export type {
  TestUser,
  AuthProfileSelection,
  AuthPermissionProfile,
  AuthScope,
};
